import './App.css';
import { Button, Drawer, Result } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AppSidebar } from './components/AppSidebar';
import { BrandMark } from './components/BrandMark';
import { CommandPalette, useCommandPaletteHotkey } from './components/CommandPalette';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { Topbar } from './components/Topbar';
import { useAppDispatch } from './app/hooks';
import { fetchCountries } from './features/country/countrySlice';
import { useAuth, hasAuthParams } from "react-oidc-context";
import { useTranslate } from './i18n';
import { isAllowedToSignIn } from './utils/jwt';
import { brand } from './config/brand';

/** Remembers the rail's width between sessions; it is a workspace preference. */
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

function App() {

  const dispatch = useAppDispatch();
  const auth = useAuth();
  const location = useLocation();
  const t = useTranslate();

  // This console belongs to organization admins. A global administrator has
  // their own, so their token is turned away here rather than half-working:
  // every screen below calls `/organizations/my/administration/...`, which
  // the API only answers for a token carrying the organization claims.
  const isOrganizationAdmin = isAllowedToSignIn(auth.user?.access_token);

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useCommandPaletteHotkey(openPalette);

  const toggleCollapsed = () => {
    setCollapsed(current => {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(!current));
      return !current;
    });
  };

  useEffect(() => {
    if (!hasAuthParams() &&
        !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
      auth.signinRedirect();}
  },[auth.isAuthenticated, auth.activeNavigator, auth.isLoading, auth.signinRedirect]);


  useEffect(() => {
    if (auth.isAuthenticated && isOrganizationAdmin) {
      const token = auth.user?.access_token;
      localStorage.setItem('token', token ?? '');
    } else if (!auth.isLoading) {
      // A rejected session must not leave a usable token behind. The check
      // waits for the restore to finish: clearing mid-restore would strand a
      // request issued before the stored session came back.
      localStorage.removeItem('token');
    }
  }, [auth, isOrganizationAdmin]);

  useEffect(() =>  {
    return auth.events.addAccessTokenExpiring(async () => {
      await auth.signinSilent();
      const token = auth.user?.access_token;
      if (isAllowedToSignIn(token)) {
        localStorage.setItem('token', token ?? '');
      } else {
        localStorage.removeItem('token');
      }
    })
  }, [auth.events,auth.signinSilent]);


  useEffect(() => {
    if (isOrganizationAdmin) {
      dispatch(fetchCountries());
    }
  }, [isOrganizationAdmin])

  const signOut = () => auth.signoutRedirect({
    post_logout_redirect_uri: window.location.origin,
    id_token_hint: auth.user?.id_token
  });

  if (auth.isAuthenticated && !isOrganizationAdmin) {
    return (
      <div className="App">
        {/* No topbar on this screen, so the language picker stands on its
            own: an admin who cannot read the refusal cannot act on it. */}
        <LanguageSwitcher variant="standalone" />
        <div className="app-standalone">
          <Result
            status="403"
            title={t("access.denied.title")}
            subTitle={t("access.denied.subtitle")}
            extra={
              <Button type="primary" onClick={signOut}>
                {t("common.signOut")}
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="App">
        <div className="boot-screen">
          <span className="boot-screen__mark">
            <BrandMark />
          </span>
          <span className="boot-screen__label">{brand.name}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="app-shell">
        <AppSidebar collapsed={collapsed} onToggleCollapse={toggleCollapsed} />

        {/* Below the `lg` breakpoint the rail is hidden and the same navigation
            is reached through this drawer. It used to collapse to nothing with
            no trigger, which left the app unnavigable on a narrow screen. */}
        <Drawer
          placement="left"
          closable={false}
          width={272}
          bodyStyle={{ padding: 0 }}
          visible={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <AppSidebar variant="drawer" onNavigate={() => setDrawerOpen(false)} />
        </Drawer>

        <div className="app-main">
          <Topbar
            onOpenMenu={() => setDrawerOpen(true)}
            onOpenCommandPalette={openPalette}
          />
          <main className="app-content">
            {/* Keyed on the section, not the full path: switching sections
                replays the entrance, while drilling into a record inside one
                keeps the subtree mounted. */}
            <div className="page" key={location.pathname.split("/")[1] ?? ""}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

export default App;
